import { Button, Card, Chip, EmptyState, ListRow } from '@brain/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  BellRing,
  CalendarClock,
  CheckSquare,
  ClipboardList,
  FileText,
  Mail,
  Merge,
  Pencil,
  Phone,
  User,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../components/meetings/MeetingList.tsx'
import { CommitmentRow } from '../../components/people/CommitmentRow.tsx'
import { MergeIntoDialog } from '../../components/people/MergeDialogs.tsx'
import { PersonDialog } from '../../components/people/PersonDialog.tsx'
import { relativeDay } from '../../lib/list-date.ts'
import { personQuery } from '../../lib/m2.ts'
import { personPrepsQuery } from '../../lib/people-m4.ts'

export const Route = createFileRoute('/_app/personen/$personId')({ component: PersonPage })

function PersonPage() {
  const { t } = useTranslation()
  const { personId } = Route.useParams()
  const q = useQuery(personQuery(personId))
  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)
  const preps = useQuery(personPrepsQuery(personId))
  const p = q.data
  if (q.isError)
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState title={t('people.notFound')} description={t('people.notFoundText')} />
      </div>
    )
  if (!p) return <p className="p-8 text-[13px] text-meta">{t('common.loading')}</p>
  const iOwe = p.commitments.filter((c) => c.direction === 'i_owe' && c.status === 'open')
  const owed = p.commitments.filter((c) => c.direction === 'owed_to_me' && c.status === 'open')

  return (
    <div className="min-w-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-4 md:px-8 md:py-6">
        <Link to="/personen" className="text-[13px] text-meta hover:text-text-1">
          ← {t('nav.people')}
        </Link>
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex items-start gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-pill bg-green-bg text-green-fg">
              <User size={20} strokeWidth={1.9} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="text-[22px] text-text-1">{p.name}</h1>
              <p className="text-[13px] text-text-2">{[p.role, p.company].filter(Boolean).join(' · ')}</p>
            </div>
            <Button
              size={32}
              variant="ghost"
              aria-label={t('crm.merge.button')}
              onClick={() => setMerging(true)}
            >
              <Merge size={14} />
            </Button>
            <Button size={32} variant="ghost" aria-label={t('people.edit')} onClick={() => setEditing(true)}>
              <Pencil size={14} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-3 text-[13px] text-text-2">
            {p.emails.map((e) => (
              <span key={e} className="flex items-center gap-1">
                <Mail size={13} />
                {e}
              </span>
            ))}
            {p.phone && (
              <span className="flex items-center gap-1">
                <Phone size={13} />
                {p.phone}
              </span>
            )}
            {p.lastContactAt && (
              <span>{t('people.lastContact', { date: relativeDay(p.lastContactAt) })}</span>
            )}
          </div>
          {(p.contactDue || p.followUpOn || p.contactIntervalDays || p.aliases.length > 0) && (
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-text-2">
              {p.contactDue && (
                <Chip tone="apricot">
                  <BellRing size={12} className="mr-1 inline" />
                  {t('crm.contactDue')}
                </Chip>
              )}
              {p.followUpOn && <span>{t('crm.followUpOn', { date: relativeDay(p.followUpOn) })}</span>}
              {p.contactIntervalDays && <span>{t('crm.interval', { count: p.contactIntervalDays })}</span>}
              {p.aliases.length > 0 && <span>{t('crm.aliasesList', { list: p.aliases.join(', ') })}</span>}
            </div>
          )}
          {p.containers.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {p.containers.map((c) => (
                <Link key={c.id} to="/c/$containerId" params={{ containerId: c.id }}>
                  <Chip>{c.title}</Chip>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <section className="grid gap-4 md:grid-cols-2">
          <Card className="p-3">
            <h2 className="px-2 pb-1 meta-label">{t('commitments.iOwe')}</h2>
            {iOwe.length === 0 && <p className="px-2 py-2 text-[13px] text-meta">{t('commitments.none')}</p>}
            {iOwe.map((c) => (
              <CommitmentRow key={c.id} c={c} showPerson={false} />
            ))}
          </Card>
          <Card className="p-3">
            <h2 className="px-2 pb-1 meta-label">{t('commitments.owedToMe')}</h2>
            {owed.length === 0 && <p className="px-2 py-2 text-[13px] text-meta">{t('commitments.none')}</p>}
            {owed.map((c) => (
              <CommitmentRow key={c.id} c={c} showPerson={false} />
            ))}
          </Card>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <Card className="p-2">
            <h2 className="px-3 pt-1 pb-1 meta-label">{t('nav.meetings')}</h2>
            {p.meetings.length === 0 && (
              <p className="px-3 py-2 text-[13px] text-meta">{t('people.noMeetings')}</p>
            )}
            {p.meetings.map((m) => (
              <Link key={m.id} to="/meetings/$meetingId" params={{ meetingId: m.id }} className="block">
                <ListRow
                  leading={<CalendarClock size={16} strokeWidth={1.9} />}
                  title={m.title}
                  subtitle={formatDateTime(m.startedAt)}
                />
              </Link>
            ))}
          </Card>
          <Card className="p-2">
            <h2 className="px-3 pt-1 pb-1 meta-label">{t('people.notes')}</h2>
            {p.notes.length === 0 && <p className="px-3 py-2 text-[13px] text-meta">{t('people.noNotes')}</p>}
            {p.notes.map((n) => (
              <Link key={n.id} to="/n/$noteId" params={{ noteId: n.id }} className="block">
                <ListRow
                  leading={<FileText size={16} strokeWidth={1.9} />}
                  title={n.title || t('note.untitled')}
                  trailing={<span className="text-[11px] text-meta">{relativeDay(n.updatedAt)}</span>}
                />
              </Link>
            ))}
          </Card>
        </section>
        <section className="grid gap-4 md:grid-cols-2">
          <Card className="p-2">
            <h2 className="px-3 pt-1 pb-1 meta-label">{t('crm.tasks')}</h2>
            {p.tasks.length === 0 && <p className="px-3 py-2 text-[13px] text-meta">{t('crm.noTasks')}</p>}
            {p.tasks.map((x) => (
              <Link key={x.id} to="/tasks" search={{ task: x.id }} className="block">
                <ListRow
                  leading={<CheckSquare size={16} strokeWidth={1.9} />}
                  title={x.title}
                  subtitle={[x.containerTitle, x.dueDate ? relativeDay(x.dueDate) : null]
                    .filter(Boolean)
                    .join(' · ')}
                />
              </Link>
            ))}
          </Card>
          <Card className="p-2">
            <h2 className="px-3 pt-1 pb-1 meta-label">{t('crm.prep.title')}</h2>
            {(preps.data ?? []).length === 0 && (
              <p className="px-3 py-2 text-[13px] text-meta">{t('crm.prep.nonePerson')}</p>
            )}
            {(preps.data ?? []).map((b) => (
              <Link
                key={b.id}
                to="/vorbereitung/$prepId"
                params={{ prepId: b.id as string }}
                className="block"
              >
                <ListRow
                  leading={<ClipboardList size={16} strokeWidth={1.9} />}
                  title={b.event.title}
                  subtitle={formatDateTime(b.event.startsAt)}
                />
              </Link>
            ))}
          </Card>
        </section>
        {editing && <PersonDialog person={p} onClose={() => setEditing(false)} />}
        {merging && <MergeIntoDialog personId={p.id} name={p.name} onClose={() => setMerging(false)} />}
      </div>
    </div>
  )
}
